import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { Store } from "@ngxs/store";
import { Observable, Subject, Subscription } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  take,
} from "rxjs/operators";
import { ILearningObjectiveFilters } from "../model/interfaces";
import { DashboardState } from "../store/dashboard.state";
import {
  LearningObjectiveOrderState,
  ListData,
} from "../store/learning-objective-order.state";
import { LearningObjectivesState } from "../store/learning-objectives.state";
import {
  CitoApplicationModule,
  LearningObjective,
  LearningObjectiveOrder,
} from "./../model/model";
import {
  CheckboxModel,
  CheckUncheckAllDomainFiltersAction,
  CheckUncheckAllGradeFiltersAction,
  CheckUncheckAllTagFiltersAction,
  FilterByLoFilterAction,
  LearningObjectiveData,
  SetSelectedLearningObjectiveOrderAction,
  UpdateAllSubjectCheckboxAction,
  UpdateDomainFiltersAction,
  UpdateGradeFiltersAction,
  UpdateLearningrouteFiltersAction,
  UpdateReferencelevelFiltersAction,
  UpdateSubjectCheckboxAction,
  UpdateTagFiltersAction,
} from "./../store/learning-objectives.state";

import { AsyncPipe, CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { BsDropdownModule } from "ngx-bootstrap/dropdown";
import { PopoverModule } from "ngx-bootstrap/popover";

@Component({
  selector: "app-learning-objective-filters",
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AsyncPipe,
    FormsModule,
    BsDropdownModule,
    PopoverModule,
  ],
  templateUrl: "./learning-objective-filters.component.html",
  styleUrls: ["./learning-objective-filters.component.css"],
})
export class LearningObjectiveFiltersComponent implements OnDestroy, OnInit {
  generatedSubjects$: Observable<Array<any>>;
  generatedSubjects2$: Observable<Array<any>>;
  learningObjectiveFilters$: Observable<ILearningObjectiveFilters>;

  searchText: string = "";
  modelChangeSubscription: Subscription;
  modelChanged$: Subject<string> = new Subject<string>();
  checkedSubjectFilters: Array<any>;
  allSubjectsChecked: boolean;
  showLearningObjFilters: boolean;
  learningObjectiveFilter: number;
  usingOwnList: boolean;

  learningObjectiveOrders$: Observable<ListData>;
  learningObjectiveOrderTemplates$: Observable<LearningObjectiveOrder[]>;
  learningObjectiveData$: Observable<LearningObjectiveData>;
  subjectsInList$: Observable<LearningObjectiveData["subjectsInList"]>;
  selectedLearningObjectiveOrder$: Observable<any>;
  selectedCitoModule$: Observable<CitoApplicationModule>;

  allGradeLevelsSelected$: Observable<boolean>;
  allDomainsSelected$: Observable<boolean>;
  allTagsSelected$: Observable<boolean>;
  refLevelPlaceholder$: Observable<string>;
  learningroutePlaceholder$: Observable<string>;
  lvsPlaceholder$: Observable<string>;
  gradePlaceholder$: Observable<string>;
  domainPlaceholder$: Observable<string>;

  router: string;

  @Input() disabled = false;
  @Input() canToggleRelatedOnly = false;
  @Input() placeholdertext: string;

  @Output() filterSelectedAndRelatedOnly = new EventEmitter();
  @Output() filterText = new EventEmitter<string>();

  constructor(private store: Store, private _router: Router) {
    _router.events.subscribe((val) => {
      if (val instanceof NavigationEnd && val.url === "/domains") {
        this.resetFilters();
      }
    });

    this.router = _router.url;

    // Update router property when navigation occurs
    _router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.router = event.url;
      });
    this.usingOwnList = false;
    this.learningObjectiveFilter = 1;
    this.generatedSubjects$ = this.store
      .select(LearningObjectivesState.learningObjectives)
      .pipe(map((l) => l.subjectsInList));
    this.generatedSubjects2$ = this.store.select(
      LearningObjectivesState.subjectsInLearningObjectives
    );
    this.learningObjectiveOrders$ = this.store.select(
      LearningObjectiveOrderState.myLists
    );
    this.learningObjectiveOrderTemplates$ = this.store.select(
      LearningObjectiveOrderState.templateLists
    );
    this.learningObjectiveFilters$ = this.store.select(
      LearningObjectivesState.filters
    );
    this.selectedCitoModule$ = this.store.select(DashboardState.citomodule);
    this.allDomainsSelected$ = this.store.select(
      LearningObjectivesState.allDomainsSelected
    );
    this.allGradeLevelsSelected$ = this.store.select(
      LearningObjectivesState.allGradeLevelsSelected
    );
    this.allTagsSelected$ = this.store.select(
      LearningObjectivesState.allTagsSelected
    );

    this.refLevelPlaceholder$ = this.store.select(
      LearningObjectivesState.refLevelPlaceholder
    );
    this.learningroutePlaceholder$ = this.store.select(
      LearningObjectivesState.learningRoutePlaceholder
    );
    this.lvsPlaceholder$ = this.store.select(
      LearningObjectivesState.lvsPlaceholder
    );
    this.gradePlaceholder$ = this.store.select(
      LearningObjectivesState.yearGroupPlaceholder
    );
    this.domainPlaceholder$ = this.store.select(
      LearningObjectivesState.domainPlaceholder
    );
    this.learningObjectiveData$ = this.store
      .select(LearningObjectivesState.learningObjectives)
      .pipe(
        filter((ls) => {
          if (ls) return !!ls.learningObjectivesInList;
        })
      );
    this.subjectsInList$ = this.store.select(
      LearningObjectivesState.subjectsInList
    );

    this.showLearningObjFilters = true;
    this.allSubjectsChecked = true;
    this.checkedSubjectFilters = [];

    this.modelChangeSubscription = this.modelChanged$
      .pipe(
        debounceTime(300), // wait 300ms after the last event before emitting last event
        distinctUntilChanged() // only emit if value is different from previous value
      )
      .subscribe((txt) => this.filterText.emit(txt));
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    if (this.modelChangeSubscription) {
      this.modelChangeSubscription.unsubscribe();
    }
  }

  loPresent(lo: LearningObjective, los: LearningObjective[]) {
    return los.findIndex((obj) => obj.id === lo.id) > -1;
  }

  changed = (text: string) => this.modelChanged$.next(text);
  focus = ($event: FocusEvent) => {
    // Allow normal focus behavior for the search input
  };
  setSelectedLearningObjectiveOrder = (id: string) => {
    this.learningObjectiveFilter = 4;
    this.store.dispatch(
      new FilterByLoFilterAction(this.learningObjectiveFilter)
    );
    this.store.dispatch(new SetSelectedLearningObjectiveOrderAction(id));
  };

  showHideLOFilters = () => {
    this.showLearningObjFilters = !this.showLearningObjFilters;
  };

  checkUncheckAllGroups = ($event: Event, checked: boolean) => {
    $event.stopPropagation();
    this.store.dispatch(new CheckUncheckAllGradeFiltersAction(checked));
  };

  checkUncheckGroup = ($event: Event, checked: boolean, id: number) => {
    $event.stopPropagation();
    this.store.dispatch(
      new UpdateGradeFiltersAction({ id: id, value: checked })
    );
  };

  checkUncheckAllDomains = ($event: Event, checked: boolean) => {
    $event.stopPropagation();
    this.store.dispatch(new CheckUncheckAllDomainFiltersAction(checked));
  };

  checkUncheckDomain = ($event: Event, checked: boolean, id: number) => {
    $event.stopPropagation();
    this.store.dispatch(
      new UpdateDomainFiltersAction({ id: id, value: checked })
    );
  };

  checkUncheckAllTags = ($event: Event, checked: boolean) => {
    $event.stopPropagation();

    if (this.learningObjectiveFilter !== 3) {
      this.learningObjectiveFilter = 3;
      this.store.dispatch(
        new FilterByLoFilterAction(this.learningObjectiveFilter)
      );
    }

    this.store.dispatch(
      new FilterByLoFilterAction(this.learningObjectiveFilter)
    );

    this.store.dispatch(new CheckUncheckAllTagFiltersAction(checked));
  };

  checkUncheckTag = ($event: Event, checked: boolean, id: number) => {
    $event.stopPropagation();
    if (this.learningObjectiveFilter !== 3) {
      this.learningObjectiveFilter = 3;
      this.store.dispatch(
        new FilterByLoFilterAction(this.learningObjectiveFilter)
      );
    }
    this.store.dispatch(new UpdateTagFiltersAction({ id: id, value: checked }));
  };

  checkUncheckAllSubjects = ($event: Event) => {
    $event.stopPropagation();
    this.store.dispatch(new UpdateAllSubjectCheckboxAction());

    this.store
      .select(LearningObjectivesState.subjectsInList)
      .pipe(take(1))
      .subscribe((state) => {
        const allChecked = state.every((s: CheckboxModel) => s.checked);
        this.allSubjectsChecked = allChecked;
      });
  };

  checkUncheckSingleSubject = (subject: CheckboxModel, $event: Event) => {
    $event.stopPropagation();

    this.store.dispatch(new UpdateSubjectCheckboxAction(subject));

    this.store
      .select(LearningObjectivesState.subjectsInList)
      .pipe(take(1))
      .subscribe((state) => {
        const allChecked = state.every((s: CheckboxModel) => s.checked);
        this.allSubjectsChecked = allChecked;
      });
  };

  changeReferenceLevelAP = ($event: Event, checked: boolean, id: number) => {
    $event.stopPropagation();

    if (this.learningObjectiveFilter !== 1) {
      this.learningObjectiveFilter = 1;
      this.store.dispatch(
        new FilterByLoFilterAction(this.learningObjectiveFilter)
      );
    }
    this.store.dispatch(
      new UpdateReferencelevelFiltersAction({ id: id, value: checked })
    );
  };

  changeReferenceLevelLR = ($event: Event, checked: boolean, id: number) => {
    $event.stopPropagation();

    if (this.learningObjectiveFilter !== 2) {
      this.learningObjectiveFilter = 2;
      this.store.dispatch(
        new FilterByLoFilterAction(this.learningObjectiveFilter)
      );
    }
    this.store.dispatch(
      new UpdateLearningrouteFiltersAction({ id: id, value: checked })
    );
  };

  changeLoFilter = ($event: Event) => {
    $event.stopPropagation();

    this.store.dispatch(
      new FilterByLoFilterAction(this.learningObjectiveFilter)
    );
    let list: ListData;
    this.learningObjectiveOrders$.subscribe((data) => {
      list = data;
    });

    if (this.learningObjectiveFilter === 4) {
      if (!this.usingOwnList) {
        this.store.dispatch(
          new SetSelectedLearningObjectiveOrderAction(list.myLists[0].id)
        );
        this.usingOwnList = true;
      }
    } else {
      if (this.usingOwnList) {
        this.store.dispatch(
          new SetSelectedLearningObjectiveOrderAction(list.templateLists[0].id)
        );
        this.usingOwnList = false;
      }
    }
  };

  resetFilters = () => {
    let list: ListData;
    this.learningObjectiveOrders$.subscribe((data) => {
      list = data;
    });
    this.learningObjectiveFilter = 1;
    this.allSubjectsChecked = true;

    this.store.dispatch(new CheckUncheckAllDomainFiltersAction(true));
    this.store.dispatch(new CheckUncheckAllGradeFiltersAction(true));
    this.store.dispatch(new CheckUncheckAllTagFiltersAction(true));
    this.store.dispatch(new UpdateAllSubjectCheckboxAction());
    this.store.dispatch(new FilterByLoFilterAction(1));
  };

  getSubjectPlaceholder$ = this.store
    .select(LearningObjectivesState.subjectsInList)
    .pipe(
      map((subjects) => {
        const allChecked = subjects.every((sub) => sub.checked);
        const checkedSubjects = subjects.filter((sub) => sub.checked);
        if (allChecked) return "Alle";
        if (checkedSubjects.length > 0)
          return `${checkedSubjects.length} filters actief`;
        return "Geen filters actief";
      })
    );
}
